import { AppThunk } from 'src/store/store';
import {
  setRenewableLicenses,
  setRenewableLicensesFailure,
  setRenewableLicensesLoading,
  setUserLicenses,
  setUserLicensesFailure,
  setUserLicensesLoading,
} from './userLicensesSlice';
import api from 'src/api';
import { selectCurrentIDToken } from '../auth/authSelectors';
import { License } from 'src/types/licenses';

export const getUserLicensesAsync = (
  forceRefresh: boolean = false
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    dispatch(setUserLicensesLoading(true));

    const fetchPage = async (refresh: boolean): Promise<void> => {
      const accessToken = selectCurrentIDToken(getState());
      let contToken = 0;
      let pagesLoaded: number[] = [];
      let currentData: License[] = [];
      if (!refresh) {
        contToken = getState().userLicenses.userLicenses.nextContToken || 0;
        pagesLoaded = getState().userLicenses.userLicenses.pagesLoaded || [];
        currentData = getState().userLicenses.userLicenses.data;
        if (!contToken && contToken !== 0) {
          dispatch(setUserLicensesLoading(false));
          return;
        }
        if (pagesLoaded.includes(contToken)) {
          dispatch(setUserLicensesLoading(false));
          return;
        }
      }
      const data = await api.getUserLicenses(accessToken, contToken);
      dispatch(
        setUserLicenses({
          data: [...currentData, ...data.data],
          contToken,
          nextContToken: data.contToken,
          pagesLoaded: [...pagesLoaded, contToken],
        })
      );
      if (data.contToken !== null) {
        // Continue fetching subsequent pages of the same operation without
        // re-dispatching setUserLicensesLoading(true) — doing so flashes
        // already-rendered UI (which reacts to `loading`) back to a loading
        // state between every page instead of only before the first page.
        await fetchPage(false);
      }
    };

    try {
      await fetchPage(forceRefresh);
    } catch (error) {
      console.error('error loading user licenses', error);
      dispatch(
        setUserLicensesFailure(
          'Something went wrong while fetching user licenses.'
        )
      );
    }
  };
};

export const getUsersRenewableLicensesAsync = (
  forceRefresh: boolean = false
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    dispatch(setRenewableLicensesLoading(true));

    const fetchPage = async (refresh: boolean): Promise<void> => {
      let contToken = 0;
      let pagesLoaded: number[] = [];
      let currentData: License[] = [];
      if (!refresh) {
        currentData = getState().userLicenses.renewableLicenses.data;
        contToken =
          getState().userLicenses.renewableLicenses.nextContToken || 0;

        pagesLoaded =
          getState().userLicenses.renewableLicenses.pagesLoaded || [];
        if (!contToken && contToken !== 0) {
          dispatch(setRenewableLicensesLoading(false));
          return;
        }
        if (pagesLoaded.includes(contToken)) {
          dispatch(setRenewableLicensesLoading(false));
          return;
        }
      }

      const accessToken = selectCurrentIDToken(getState());
      const data = await api.getUserRenewableLicenses(accessToken, contToken);

      dispatch(
        setRenewableLicenses({
          data: [...currentData, ...data.data],
          contToken,
          nextContToken: data.contToken,
          pagesLoaded: [...pagesLoaded, contToken],
        })
      );

      if (data.contToken !== null) {
        // See getUserLicensesAsync above — don't re-flip loading between
        // pages of the same operation.
        await fetchPage(false);
      }
    };

    try {
      await fetchPage(forceRefresh);
    } catch (error) {
      console.error('error loading renewable licenses', error);
      dispatch(
        setRenewableLicensesFailure(
          'Something went wrong while fetching renewable licenses.'
        )
      );
    }
  };
};
